import { isoDate as melDate } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertCircle, CheckCircle2, CircleSlash, Download, RefreshCw, TimerOff, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/intl";
import { getCached, setCached } from "@/lib/pageStateCache";

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return melDate(d);
}

const CATEGORIES = [
  {
    key: "checked_in",
    label: "Checked In",
    icon: CheckCircle2,
    accent: "border-emerald-300 bg-emerald-50 text-emerald-800",
    trigger: "data-[state=active]:bg-emerald-600 data-[state=active]:text-white",
  },
  {
    key: "dna",
    label: "DNA",
    icon: CircleSlash,
    accent: "border-rose-300 bg-rose-50 text-rose-800",
    trigger: "data-[state=active]:bg-rose-600 data-[state=active]:text-white",
  },
  {
    key: "lcxl",
    label: "LCXL",
    icon: TimerOff,
    accent: "border-amber-300 bg-amber-50 text-amber-900",
    trigger: "data-[state=active]:bg-amber-600 data-[state=active]:text-white",
  },
  {
    key: "unlabelled",
    label: "Unlabelled",
    icon: HelpCircle,
    accent: "border-zinc-300 bg-zinc-50 text-zinc-700",
    trigger: "data-[state=active]:bg-zinc-900 data-[state=active]:text-white",
  },
];

function formatDate(iso) {
  if (!iso || iso.length < 10) return iso || "—";
  return iso.slice(0, 10).split("-").reverse().join("/");
}

export default function UnpaidReportTab() {
  const cached = getCached("unpaid-report");
  const [minDate, setMinDate] = useState(cached?.minDate ?? isoDaysAgo(7));
  const [maxDate, setMaxDate] = useState(cached?.maxDate ?? isoDaysAgo(1));
  const [data, setData] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(cached?.category ?? "checked_in");

  useEffect(() => {
    setCached("unpaid-report", { minDate, maxDate, data, category, ts: Date.now() });
  }, [minDate, maxDate, data, category]);

  async function runReport() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/unpaid-report", {
        params: { min_date: minDate, max_date: maxDate },
        timeout: 120000,
      });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load unpaid report", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!data) return;
    const cols = ["Date", "Time", "Client", "Phone", "Email", "Class", "Instructor", "Amount owed", "Label"];
    const lines = [cols.join(",")];
    const safe = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const labelName = (cat) => CATEGORIES.find((c) => c.key === cat)?.label || cat;
    for (const r of data.rows) {
      lines.push(
        [
          r.date_display, r.time_display, r.client_name, r.phone, r.email,
          r.class_type, r.calendar, r.amount_owed?.toFixed?.(2) ?? r.amount_owed,
          labelName(r.category),
        ].map(safe).join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `unpaid_appointments_${minDate}_to_${maxDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("CSV downloaded");
  }

  const totals = data?.totals;

  const visibleRows = useMemo(() => {
    if (!data) return [];
    return data.by_category?.[category] ?? [];
  }, [data, category]);

  const visibleTotal = totals?.[category]?.amount ?? 0;

  return (
    <div className="space-y-6" data-testid="unpaid-report-tab">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Chasing money
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Unpaid appointments
        </h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
          Active appointments with a price where <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">paid ≠ yes</code>.
          Prepaid-credit redemptions register as paid in Acuity and are excluded automatically.
        </p>
      </div>

      <Card className="p-5 sm:p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">From</Label>
            <Input
              type="date"
              value={minDate}
              onChange={(e) => setMinDate(e.target.value)}
              className="border-zinc-300 mt-1.5 w-full sm:w-44 font-mono-num"
              data-testid="unpaid-min-date"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">To</Label>
            <Input
              type="date"
              value={maxDate}
              onChange={(e) => setMaxDate(e.target.value)}
              className="border-zinc-300 mt-1.5 w-full sm:w-44 font-mono-num"
              data-testid="unpaid-max-date"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
            <Button
              size="sm" variant="outline" className="border-zinc-300"
              onClick={() => { setMinDate(isoDaysAgo(7)); setMaxDate(isoDaysAgo(1)); }}
              data-testid="unpaid-preset-7d"
            >
              Last 7 days
            </Button>
            <Button
              size="sm" variant="outline" className="border-zinc-300"
              onClick={() => { setMinDate(isoDaysAgo(30)); setMaxDate(isoDaysAgo(1)); }}
              data-testid="unpaid-preset-30d"
            >
              Last 30 days
            </Button>
          </div>
          <Button
            onClick={runReport}
            disabled={loading}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="unpaid-run-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            {loading ? "Running…" : data ? "Re-run" : "Run report"}
          </Button>
          <Button
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100 lg:ml-auto"
            disabled={!data?.count || loading}
            onClick={exportCsv}
            data-testid="unpaid-export-csv"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </Card>

      {error && (
        <div
          data-testid="unpaid-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {!data && !loading && !error && (
        <Card
          data-testid="unpaid-idle-state"
          className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center"
        >
          <AlertCircle className="h-8 w-8 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
            Pick a date range and click Run report
          </h2>
          <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
            Scans Acuity for active appointments in the window and lists any with an outstanding balance.
          </p>
        </Card>
      )}

      {loading && !data && (
        <Card className="p-12 rounded-lg border border-zinc-200 shadow-none text-center">
          <RefreshCw className="h-8 w-8 text-zinc-400 mx-auto mb-3 animate-spin" strokeWidth={1.5} />
          <p className="mt-2 text-sm text-zinc-600 font-mono-num">scanning Acuity…</p>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => {
              const t = totals?.[c.key] || { count: 0, amount: 0 };
              const Icon = c.icon;
              return (
                <Card
                  key={c.key}
                  className={cn(
                    "p-4 rounded-lg border shadow-none cursor-pointer transition-colors",
                    category === c.key
                      ? "border-zinc-900 bg-zinc-950 text-white"
                      : "border-zinc-200 hover:border-zinc-400",
                  )}
                  onClick={() => setCategory(c.key)}
                  data-testid={`unpaid-cat-${c.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={cn(
                      "text-2xs uppercase tracking-[0.15em] font-semibold",
                      category === c.key ? "text-zinc-400" : "text-zinc-500",
                    )}>
                      {c.label}
                    </div>
                    <Icon className={cn(
                      "h-4 w-4",
                      category === c.key ? "text-zinc-400" : "text-zinc-400",
                    )} strokeWidth={1.75} />
                  </div>
                  <div className="mt-3 font-mono-num text-2xl">
                    {t.count}
                  </div>
                  <div className={cn(
                    "text-xs font-mono-num mt-0.5",
                    category === c.key ? "text-zinc-400" : "text-zinc-500",
                  )}>
                    {fmtMoney(t.amount)}
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                  {CATEGORIES.find((c) => c.key === category)?.label}
                </div>
                <div className="mt-1 text-sm text-zinc-600 font-mono-num">
                  {visibleRows.length} appointment{visibleRows.length === 1 ? "" : "s"} · {fmtMoney(visibleTotal)}
                </div>
              </div>
              <div className="text-xs text-zinc-500 font-mono-num">
                {data.count} total · {fmtMoney(data.total_amount_owed)} owed
              </div>
            </div>

            <Tabs value={category} onValueChange={setCategory}>
              <TabsList className="bg-zinc-100 m-4" data-testid="unpaid-cat-tabs">
                {CATEGORIES.map((c) => (
                  <TabsTrigger
                    key={c.key}
                    value={c.key}
                    className={cn("text-xs", c.trigger)}
                    data-testid={`unpaid-tab-${c.key}`}
                  >
                    {c.label} ({totals?.[c.key]?.count ?? 0})
                  </TabsTrigger>
                ))}
              </TabsList>

              {CATEGORIES.map((c) => (
                <TabsContent key={c.key} value={c.key} className="mt-0">
                  <UnpaidTable
                    rows={data.by_category[c.key] || []}
                    testIdPrefix={`unpaid-${c.key}`}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </Card>
        </>
      )}
    </div>
  );
}

function UnpaidTable({ rows, testIdPrefix }) {
  return (
    <div className="overflow-x-auto thin-scroll max-h-[600px]">
      <Table>
        <TableHeader className="sticky top-0 bg-zinc-50 z-10">
          <TableRow>
            {["Date/time", "Client", "Class", "Instructor", "Contact", "Amount"].map((h) => (
              <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">
                No unpaid appointments in this bucket — nice work!
              </TableCell>
            </TableRow>
          )}
          {rows.map((r, i) => (
            <TableRow key={r.id ?? i} data-testid={`${testIdPrefix}-row-${i}`}>
              <TableCell className="font-mono-num text-xs text-zinc-700 whitespace-nowrap">
                {r.date_display}
                <div className="text-2xs text-zinc-500">{r.time_display}</div>
              </TableCell>
              <TableCell className="text-sm text-zinc-950 font-medium">{r.client_name}</TableCell>
              <TableCell className="text-sm text-zinc-700">{r.class_type || "—"}</TableCell>
              <TableCell className="text-xs text-zinc-600">{r.calendar || "—"}</TableCell>
              <TableCell className="text-xs text-zinc-700">
                {r.email && (
                  <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a>
                )}
                {r.phone && (
                  <div className="font-mono-num text-2xs text-zinc-500 mt-0.5">{r.phone}</div>
                )}
                {!r.email && !r.phone && <span className="text-zinc-400 italic">no contact</span>}
              </TableCell>
              <TableCell className="font-mono-num text-sm text-zinc-950 whitespace-nowrap">
                {fmtMoney(r.amount_owed)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 0 && (
        <div className="px-4 py-2 border-t border-zinc-100 text-2xs text-zinc-500 font-mono-num text-right">
          {rows.length} row{rows.length === 1 ? "" : "s"} · {formatDate(rows[rows.length - 1]?.datetime)} → {formatDate(rows[0]?.datetime)}
        </div>
      )}
    </div>
  );
}
